import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import SideBar from '../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../hooks/usePageLayout';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { useSecurityAddonPageAccess } from '../../../hooks/useSecurityAddonPageAccess';
import AuditLogTab from './AuditLogTab';
import IpRestrictionTab from './IpRestrictionTab';
import SamlTab from './SamlTab';
import MetaTitle from '../../utils/MetaTitle';
import MfaTab from './MfaTab';
import {
  securitySettingsTab,
  securitySettingsTabLabel,
  type SecuritySettingsTab,
} from '../../../constants/enum';
import { gray40, gray80, gray100, red50, white } from '../../../constants/colors';
import {
  boldXxl,
  regularMd,
  regularSm,
  regularXs,
  spHeaderTitle,
} from '../../../constants/location';

const SECURITY_SETTINGS_TITLE = 'セキュリティ設定';

// タブの表示順（Issue 番号順: IP制限 #2982 → SAML #2983 → MFA #2984 → 監査ログ #2985）
const TAB_ORDER: SecuritySettingsTab[] = [
  securitySettingsTab.ipRestriction,
  securitySettingsTab.saml,
  securitySettingsTab.mfa,
  securitySettingsTab.auditLog,
];

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  tabAndButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '0 32px',
    '@media screen and (max-width:960px)': {
      flexWrap: 'wrap',
      margin: '0 16px',
      width: 'calc(100% - 32px)',
    },
  },
  tabWrapper: {
    display: 'flex',
    gap: '12px',
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  tab: {
    alignItems: 'center',
    backgroundColor: gray40,
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    height: '24px',
    justifyContent: 'center',
    padding: '0 12px',
    '@media screen and (max-width:960px)': {
      height: 'auto',
      minHeight: '24px',
      padding: '2px 8px',
      boxSizing: 'border-box',
    },
  },
  tabActive: {
    backgroundColor: gray100,
  },
  tabText: {
    ...regularXs,
    color: gray100,
    whiteSpace: 'nowrap',
  },
  tabTextActive: {
    color: white,
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '24px 32px 32px',
    minHeight: '560px',
    padding: '24px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: '16px 0 24px',
      padding: '16px',
      width: '100%',
    },
  },
  messageWrapper: {
    margin: '24px 32px',
    padding: '24px',
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    '@media screen and (max-width:960px)': {
      margin: '16px',
      padding: '16px',
    },
  },
  messageText: {
    ...regularMd,
    color: gray100,
  },
  errorText: {
    ...regularSm,
    color: red50,
  },
  notAvailableLabel: {
    ...regularSm,
    color: gray80,
    marginBottom: '8px',
  },
  placeholderLabel: {
    ...regularMd,
    color: gray80,
  },
};

/**
 * 設定 > セキュリティ 画面（#2982-2985）。
 * セキュリティアドオン購入済テナントの companyAdmin（または運営代理）のみ閲覧可能。
 * IP制限 / SAML SSO / MFA / 監査ログ の 4 機能を内部タブで束ねるコンテナ。
 * 各タブの中身は後続の機能ブランチ（#2982-2985）で実装する。
 */
const SecuritySettingsPage: React.FC = () => {
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { loading, errorText, hasSecurityAddon, canAccess, targetCompanyId, effectiveCompanyId } =
    useSecurityAddonPageAccess();
  const [selectedTab, setSelectedTab] = useState<SecuritySettingsTab>(
    securitySettingsTab.ipRestriction,
  );

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle={SECURITY_SETTINGS_TITLE} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box sx={layoutStyles.header}>
          <Typography sx={styles.headerTitle}>{SECURITY_SETTINGS_TITLE}</Typography>
        </Box>

        {loading ? (
          <CustomLoading loading />
        ) : errorText ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.errorText}>{errorText}</Typography>
          </Box>
        ) : !hasSecurityAddon ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.notAvailableLabel}>セキュリティアドオン 未契約</Typography>
            <Typography sx={styles.messageText}>
              この企業ではセキュリティアドオンが有効化されていません。利用を希望する場合は事務局までお問い合わせください。
            </Typography>
          </Box>
        ) : !canAccess ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.errorText}>この画面を表示する権限がありません。</Typography>
          </Box>
        ) : (
          <>
            <Box sx={styles.tabAndButtonWrapper}>
              <Box sx={styles.tabWrapper}>
                {TAB_ORDER.map(tab => (
                  <Box
                    key={tab}
                    sx={[styles.tab, selectedTab === tab && styles.tabActive]}
                    onClick={(): void => setSelectedTab(tab)}
                  >
                    <Typography sx={[styles.tabText, selectedTab === tab && styles.tabTextActive]}>
                      {securitySettingsTabLabel[tab]}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={styles.tableSection}>
              {selectedTab === securitySettingsTab.ipRestriction ? (
                <IpRestrictionTab companyId={targetCompanyId ?? undefined} />
              ) : selectedTab === securitySettingsTab.mfa ? (
                <MfaTab companyId={effectiveCompanyId ?? undefined} />
              ) : selectedTab === securitySettingsTab.auditLog ? (
                <AuditLogTab companyId={effectiveCompanyId ?? undefined} />
              ) : selectedTab === securitySettingsTab.saml ? (
                <SamlTab companyId={effectiveCompanyId ?? undefined} />
              ) : (
                <Typography sx={styles.placeholderLabel}>
                  「{securitySettingsTabLabel[selectedTab]}」は準備中です。
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SecuritySettingsPage;
